/**
 * Orders — multi-variant page per the May 2026 Nav Structure doc.
 *
 * One nav item, three URL forks; page reshapes by the user's profiles
 * and (optionally) which sub-route they're on:
 *
 *   - /orders/family  — PARENT / TEACHER / STUDENT (consumer-side)
 *   - /orders/school  — SCHOOL_ADMIN (organization-side pre-forecast)
 *   - /orders/kitchen — CHEF_ADMIN / CHEF_STAFF (kitchen-fulfillment
 *                       view; absorbed the former Prepare Meals page)
 *
 * `/orders` (no fork) redirects to the user's default variant
 * (`family` > `school` > `kitchen` in that order, scoped to which the
 * user actually has). The sub-routes always render exactly one
 * variant; the multi-hat switcher at the top toggles between them.
 *
 * Plan 03 added the BranchingSwitcher strip + the URL-fork redirect
 * pattern. The `OrdersDefaultRedirect` reads `me.profileCodes` to
 * decide which fork to land on for a bare `/orders` hit; multi-hat
 * users on `/orders` are redirected to their default rather than
 * shown a stacked render (which was the v0 behavior, replaced by the
 * switcher per Plan 03's design intent).
 */

import { Calendar } from 'lucide-react'
import { useMemo } from 'react'
import { Navigate, useLocation } from 'react-router'

import { BranchingSwitcher, type BranchingVariantOption } from '@/components/branching-switcher'
import { OrdoPageContainer } from '@/components/ordo-page-container'
import { useProfile } from '@/stores/profile'

import { FamilyWeekOrdersPage } from './orders/family-week'
import { KitchenOrdersPage } from './orders/kitchen'
import { type OrdersVariant, resolveAvailableVariants } from './orders-variants'
import { Placeholder } from './placeholder'
import { SchoolOrdersView } from './school-orders'

export { resolveAvailableVariants } from './orders-variants'

/** Read the active variant from the URL: /orders/{family,school,kitchen} → that variant; /orders → null (all). */
function useUrlVariant(): OrdersVariant | null {
  const { pathname } = useLocation()
  if (pathname.endsWith('/orders/family')) {
    return 'family'
  }
  if (pathname.endsWith('/orders/school')) {
    return 'school'
  }
  if (pathname.endsWith('/orders/kitchen')) {
    return 'kitchen'
  }
  return null
}

/**
 * `/orders` (no fork) → redirect to the user's default variant.
 * Default priority order: family > school > kitchen, scoped to which
 * the user actually has.
 */
export function OrdersDefaultRedirect(): React.ReactElement | null {
  const { profile, loading } = useProfile()
  if (loading || !profile) {
    return null
  }
  const available = resolveAvailableVariants(profile.profileCodes, profile.isOrdoStaff)
  if (available.length === 0) {
    // User holds no ordering profile — render the empty state inline.
    return (
      <OrdoPageContainer>
        <Placeholder
          icon={Calendar}
          title="Nothing to order yet"
          summary="Check your email if you were just invited — that's the link to get started."
        />
      </OrdoPageContainer>
    )
  }
  return <Navigate to={`/orders/${available[0]!}`} replace />
}

export function OrdersPage() {
  const { profile } = useProfile()
  const urlVariantRaw = useUrlVariant()
  const available = useMemo(
    () => resolveAvailableVariants(profile?.profileCodes ?? [], profile?.isOrdoStaff ?? false),
    [profile?.profileCodes, profile?.isOrdoStaff],
  )

  // Deep-link guard: user navigated to /orders/family but doesn't
  // have the 'family' variant. Redirect to their default rather than
  // 404 — share-link UX wins over strict access control here, since
  // the page reshapes per profile anyway.
  if (urlVariantRaw && !available.includes(urlVariantRaw)) {
    if (available[0]) {
      return <Navigate to={`/orders/${available[0]}`} replace />
    }
  }

  // When OrdersPage is rendered with no URL fork (i.e. inline from
  // `HomePage` at `/`), pick the user's first available variant. Same
  // priority order `OrdersDefaultRedirect` uses at `/orders` — family
  // > school > kitchen, scoped to which the user actually has. This
  // unifies `/`, `/orders`, and `/orders/{variant}` so they all show
  // the same dedicated variant page for a given persona.
  const variant: OrdersVariant | null = urlVariantRaw ?? available[0] ?? null

  const switcherVariants: BranchingVariantOption[] = available.map((v) => ({
    urlFragment: v,
    key: v,
    surface: v,
  }))
  const showSwitcher = switcherVariants.length > 1

  // The variant switcher tabs sit in a horizontal bar directly under the
  // "Orders" header: the header's own bottom divider forms the bar's top
  // edge, and the bar carries its own bottom divider — so the tabs read
  // as contained between two rules. Null for single-variant users.
  const switcherBar = showSwitcher ? (
    <div className="border-b border-border">
      <BranchingSwitcher kind="orders" variants={switcherVariants} />
    </div>
  ) : null

  if (variant === null) {
    // User holds no ordering profile at all.
    return (
      <OrdoPageContainer>
        <Placeholder
          icon={Calendar}
          title="Nothing to order yet"
          summary="Check your email if you were just invited — that's the link to get started."
        />
      </OrdoPageContainer>
    )
  }

  // Plan 07 / Plan 16: each variant has a dedicated page component
  // that owns the full surface (header, lunchbox week, day/week view,
  // task strip, etc.). The BranchingSwitcher floats above so multi-hat
  // users can flip variants from any of these surfaces.
  if (variant === 'family') {
    // Full-bleed (no OrdoPageContainer) so the week nav stays fixed while the
    // day-card list scrolls — same pattern as the school surface. The page
    // renders its own OrdoPageHeader above the week nav, so the switcher bar is
    // threaded in to sit directly under that header.
    return <FamilyWeekOrdersPage switcher={switcherBar} />
  }

  if (variant === 'kitchen') {
    // chefs-on-ordo: render the chef page full-bleed (no OrdoPageContainer
    // padding) so the chefs-v2 sticky date-range header + inner scroll
    // container works the way it did in apps/chefs. The page renders
    // its own OrdoPageHeader above the date-range bar, so the switcher bar
    // is threaded in to render directly under that header rather than here
    // (where it'd land outside the full-bleed container).
    return <KitchenOrdersPage switcher={switcherBar} />
  }

  // variant === 'school'
  // Full-bleed (no OrdoPageContainer) so the week nav stays fixed while the
  // day-card list scrolls — same pattern as the kitchen surface. The page
  // renders its own OrdoPageHeader above the week nav, so the switcher bar is
  // threaded in to sit directly under that header.
  return <SchoolOrdersView switcher={switcherBar} />
}
